import { useInjection } from 'inversify-react';
import styled from 'styled-components';

import { useTranslation } from '@desygner/ui-common-translation';

import { IProjectManager } from '../../../core/viewer/project-manager';
import { TYPES } from '../../../inversify.types';
import { Status } from '../../../store/viewer/viewer.types';
import { LayoutSkeleton } from '../../components/Skeleton/LayoutSkeleton';
import { ErrorPage as ErrorPageLayout } from './ErrorPage';

const StyledErrorWrapper = styled.div`
  display: flex;
  position: absolute;
  z-index: ${({ theme }) => theme.ZIndex.ZIndex.Elevated};
  width: 100%;
  height: 100%;
  > div {
    min-height: 100vh;
  }
`;

export type ErrorPageProps = {
  status?: Status;
};

export const ErrorPage = ({ status }: ErrorPageProps) => {
  const { t } = useTranslation();
  const projectManager = useInjection<IProjectManager>(TYPES.Viewer.ProjectManager);

  function handleBackToHome() {
    return () => projectManager.quitViewer();
  }

  if (status === Status.Loading) {
    return <LayoutSkeleton />;
  }

  if (status === Status.ProjectLocked || status === Status.UnauthorizedUser) {
    return (
      <StyledErrorWrapper>
        <ErrorPageLayout
          code={401}
          pageTitle={t('error.access_denied.title')}
          message={t('error.access_denied.description')}
          imageUrl={'https://assets.fluer.com/assets/img/access-denied--viewer.png'}
          button={{
            label: t('error.button.back_to_home'),
            onClick: handleBackToHome(),
          }}
        />
      </StyledErrorWrapper>
    );
  }

  if (status === Status.ProjectNotFound) {
    return (
      <StyledErrorWrapper>
        <ErrorPageLayout
          code={404}
          pageTitle={t('error.not_found.title')}
          message={t('error.not_found.description')}
          imageUrl={'https://assets.fluer.com/assets/img/error_404--share-viewer.png'}
          button={{
            label: t('error.button.back_to_home'),
            onClick: handleBackToHome(),
          }}
        />
      </StyledErrorWrapper>
    );
  }

  return (
    <StyledErrorWrapper>
      <ErrorPageLayout
        code={500}
        pageTitle={t('error.internal_server.title')}
        message={t('error.internal_server.description')}
        imageUrl={'https://assets.fluer.com/assets/img/error_500--viewer.png'}
        button={{
          label: t('error.button.back_to_home'),
          onClick: handleBackToHome(),
        }}
      />
    </StyledErrorWrapper>
  );
};
